import { Bvh, BVH_LEAF_TRI_THRESHOLD } from './Bvh';

/**
 * Top-down binned-SAH BVH construction over an indexed triangle mesh.
 *
 * Triangles are referenced by ID (0..triCount). The builder reorders an array
 * of triangle IDs in place so each leaf's triangles occupy a contiguous range.
 * The final BVH stores this reordering as `triIds`.
 *
 * Binning: each split candidate axis is sampled at 16 bin boundaries. For each
 * bin we accumulate AABB + tri count, then sweep from each side to evaluate
 * SAH cost = leftArea*leftCount + rightArea*rightCount. The best of the 3
 * axes × 15 split positions wins. If no split beats "make this a leaf",
 * builder stops.
 *
 * Why binned instead of full SAH: full SAH is O(N log N) per node from sorting,
 * binned is O(N) per node — ~5–10× faster on the kind of meshes we have (a
 * few hundred to a few thousand triangles per template).
 */

const BIN_COUNT = 16;

// Module-scoped SAH scratch reused across every node visit. JS is
// single-threaded so re-entry isn't a concern, and BVH builds aren't called
// recursively from inside themselves. Saves ~6 allocations per node — on a
// 10k-triangle build that's tens of thousands of small allocations otherwise.
const BIN_MIN = new Float32Array(BIN_COUNT * 6);
const BIN_COUNTS = new Uint32Array(BIN_COUNT);
const LEFT_AREA = new Float32Array(BIN_COUNT);
const LEFT_COUNT = new Uint32Array(BIN_COUNT);
const RIGHT_AREA = new Float32Array(BIN_COUNT);
const RIGHT_COUNT = new Uint32Array(BIN_COUNT);

/** Per-triangle AABB + centroid, computed once at the start of the build. */
interface TriInfo {
  /** [triId 0, minX, minY, minZ, maxX, maxY, maxZ, cX, cY, cZ] x triCount — packed. */
  data: Float32Array;
}

export function buildBvh(positions: Float32Array, indices: Uint32Array | Uint16Array): Bvh {
  const triCount = (indices.length / 3) | 0;
  if (triCount === 0) {
    return makeEmptyBvh(positions, indices);
  }

  // Per-triangle precomputed data: tri AABB + centroid. Stored packed.
  // Stride 10: [triId, minX, minY, minZ, maxX, maxY, maxZ, cX, cY, cZ]
  const triData = new Float32Array(triCount * 10);
  for (let t = 0; t < triCount; t++) {
    const i0 = indices[t * 3] * 3;
    const i1 = indices[t * 3 + 1] * 3;
    const i2 = indices[t * 3 + 2] * 3;
    const x0 = positions[i0], y0 = positions[i0 + 1], z0 = positions[i0 + 2];
    const x1 = positions[i1], y1 = positions[i1 + 1], z1 = positions[i1 + 2];
    const x2 = positions[i2], y2 = positions[i2 + 1], z2 = positions[i2 + 2];
    const minX = Math.min(x0, x1, x2);
    const minY = Math.min(y0, y1, y2);
    const minZ = Math.min(z0, z1, z2);
    const maxX = Math.max(x0, x1, x2);
    const maxY = Math.max(y0, y1, y2);
    const maxZ = Math.max(z0, z1, z2);
    const o = t * 10;
    triData[o] = t;
    triData[o + 1] = minX; triData[o + 2] = minY; triData[o + 3] = minZ;
    triData[o + 4] = maxX; triData[o + 5] = maxY; triData[o + 6] = maxZ;
    triData[o + 7] = (minX + maxX) * 0.5;
    triData[o + 8] = (minY + maxY) * 0.5;
    triData[o + 9] = (minZ + maxZ) * 0.5;
  }

  const info: TriInfo = { data: triData };

  // Output buffers — overallocate to (2*triCount - 1) max nodes (binary tree
  // with `triCount` leaves has 2*triCount - 1 total nodes if every leaf holds
  // exactly one tri; with leaf threshold >1 we use fewer).
  const maxNodes = Math.max(1, 2 * triCount - 1);
  const nodeBounds = new Float32Array(maxNodes * 6);
  const nodeLeftFirst = new Int32Array(maxNodes);
  const nodeTriCount = new Uint32Array(maxNodes);
  // Pre-allocate slot 0 for the root. `buildAt` fills the slot it's given and
  // allocates both child slots consecutively before recursing — that's how
  // the sibling-implicit layout (right = left + 1) stays valid.
  const nodes = { nodeBounds, nodeLeftFirst, nodeTriCount, count: 1 };

  // Root node covers [0, triCount).
  buildAt(info, nodes, 0, 0, triCount);

  // Extract final triId reordering.
  const triIds = new Uint32Array(triCount);
  for (let t = 0; t < triCount; t++) {
    triIds[t] = triData[t * 10] | 0;
  }

  // Root bounds for downstream consumers.
  const rootBounds = {
    min: [nodeBounds[0], nodeBounds[1], nodeBounds[2]] as [number, number, number],
    max: [nodeBounds[3], nodeBounds[4], nodeBounds[5]] as [number, number, number],
  };

  return {
    nodeCount: nodes.count,
    nodeBounds: nodeBounds.slice(0, nodes.count * 6),
    nodeLeftFirst: nodeLeftFirst.slice(0, nodes.count),
    nodeTriCount: nodeTriCount.slice(0, nodes.count),
    triIds,
    positions,
    indices,
    rootBounds,
  };
}

function makeEmptyBvh(positions: Float32Array, indices: Uint32Array | Uint16Array): Bvh {
  return {
    nodeCount: 0,
    nodeBounds: new Float32Array(0),
    nodeLeftFirst: new Int32Array(0),
    nodeTriCount: new Uint32Array(0),
    triIds: new Uint32Array(0),
    positions,
    indices,
    rootBounds: { min: [0, 0, 0], max: [0, 0, 0] },
  };
}

/**
 * Fill the pre-allocated node slot `nodeIdx` with a node covering tri slots
 * `[start, end)`. If the node is internal, allocates both child slots
 * consecutively from `nodes.count` BEFORE recursing — this is what keeps
 * the sibling-implicit (right = left + 1) layout valid.
 */
function buildAt(
  info: TriInfo,
  nodes: { nodeBounds: Float32Array; nodeLeftFirst: Int32Array; nodeTriCount: Uint32Array; count: number },
  nodeIdx: number,
  start: number,
  end: number,
): void {
  const triCount = end - start;

  // Compute this node's AABB + centroid AABB.
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  let cMinX = Infinity, cMinY = Infinity, cMinZ = Infinity;
  let cMaxX = -Infinity, cMaxY = -Infinity, cMaxZ = -Infinity;
  for (let s = start; s < end; s++) {
    const o = s * 10;
    if (info.data[o + 1] < minX) minX = info.data[o + 1];
    if (info.data[o + 2] < minY) minY = info.data[o + 2];
    if (info.data[o + 3] < minZ) minZ = info.data[o + 3];
    if (info.data[o + 4] > maxX) maxX = info.data[o + 4];
    if (info.data[o + 5] > maxY) maxY = info.data[o + 5];
    if (info.data[o + 6] > maxZ) maxZ = info.data[o + 6];
    const cx = info.data[o + 7], cy = info.data[o + 8], cz = info.data[o + 9];
    if (cx < cMinX) cMinX = cx;
    if (cy < cMinY) cMinY = cy;
    if (cz < cMinZ) cMinZ = cz;
    if (cx > cMaxX) cMaxX = cx;
    if (cy > cMaxY) cMaxY = cy;
    if (cz > cMaxZ) cMaxZ = cz;
  }

  const b = nodeIdx * 6;
  nodes.nodeBounds[b] = minX;
  nodes.nodeBounds[b + 1] = minY;
  nodes.nodeBounds[b + 2] = minZ;
  nodes.nodeBounds[b + 3] = maxX;
  nodes.nodeBounds[b + 4] = maxY;
  nodes.nodeBounds[b + 5] = maxZ;

  // Leaf if at threshold.
  if (triCount <= BVH_LEAF_TRI_THRESHOLD) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeTriCount[nodeIdx] = triCount;
    return;
  }

  // Pick split axis by longest centroid extent.
  const ex = cMaxX - cMinX;
  const ey = cMaxY - cMinY;
  const ez = cMaxZ - cMinZ;
  let axis = 0;
  let extent = ex;
  let cMin = cMinX;
  if (ey > extent) { axis = 1; extent = ey; cMin = cMinY; }
  if (ez > extent) { axis = 2; extent = ez; cMin = cMinZ; }
  // Degenerate centroid bounds — fall back to leaf.
  if (extent <= 0) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeTriCount[nodeIdx] = triCount;
    return;
  }

  // Bin triangles by centroid along chosen axis. Reset shared scratch.
  const binMin = BIN_MIN;
  for (let i = 0; i < BIN_COUNT; i++) {
    const o = i * 6;
    binMin[o] = Infinity; binMin[o + 1] = Infinity; binMin[o + 2] = Infinity;
    binMin[o + 3] = -Infinity; binMin[o + 4] = -Infinity; binMin[o + 5] = -Infinity;
  }
  const binCount = BIN_COUNTS;
  binCount.fill(0);
  const scale = BIN_COUNT / extent;
  const centroidOffset = 7 + axis; // info.data centroid stride offset

  for (let s = start; s < end; s++) {
    const o = s * 10;
    const c = info.data[o + centroidOffset];
    let bi = Math.floor((c - cMin) * scale);
    if (bi < 0) bi = 0;
    if (bi >= BIN_COUNT) bi = BIN_COUNT - 1;
    binCount[bi]++;
    const bb = bi * 6;
    if (info.data[o + 1] < binMin[bb]) binMin[bb] = info.data[o + 1];
    if (info.data[o + 2] < binMin[bb + 1]) binMin[bb + 1] = info.data[o + 2];
    if (info.data[o + 3] < binMin[bb + 2]) binMin[bb + 2] = info.data[o + 3];
    if (info.data[o + 4] > binMin[bb + 3]) binMin[bb + 3] = info.data[o + 4];
    if (info.data[o + 5] > binMin[bb + 4]) binMin[bb + 4] = info.data[o + 5];
    if (info.data[o + 6] > binMin[bb + 5]) binMin[bb + 5] = info.data[o + 6];
  }

  // Evaluate SAH cost for each split position (between bins). Reuse scratch.
  const leftArea = LEFT_AREA;
  const leftCount = LEFT_COUNT;
  const rightArea = RIGHT_AREA;
  const rightCount = RIGHT_COUNT;

  let accMinX = Infinity, accMinY = Infinity, accMinZ = Infinity;
  let accMaxX = -Infinity, accMaxY = -Infinity, accMaxZ = -Infinity;
  let accCount = 0;
  for (let i = 0; i < BIN_COUNT; i++) {
    const bb = i * 6;
    if (binCount[i] > 0) {
      if (binMin[bb] < accMinX) accMinX = binMin[bb];
      if (binMin[bb + 1] < accMinY) accMinY = binMin[bb + 1];
      if (binMin[bb + 2] < accMinZ) accMinZ = binMin[bb + 2];
      if (binMin[bb + 3] > accMaxX) accMaxX = binMin[bb + 3];
      if (binMin[bb + 4] > accMaxY) accMaxY = binMin[bb + 4];
      if (binMin[bb + 5] > accMaxZ) accMaxZ = binMin[bb + 5];
      accCount += binCount[i];
    }
    leftCount[i] = accCount;
    leftArea[i] = surfaceArea(accMinX, accMinY, accMinZ, accMaxX, accMaxY, accMaxZ);
  }

  accMinX = Infinity; accMinY = Infinity; accMinZ = Infinity;
  accMaxX = -Infinity; accMaxY = -Infinity; accMaxZ = -Infinity;
  accCount = 0;
  for (let i = BIN_COUNT - 1; i >= 0; i--) {
    const bb = i * 6;
    if (binCount[i] > 0) {
      if (binMin[bb] < accMinX) accMinX = binMin[bb];
      if (binMin[bb + 1] < accMinY) accMinY = binMin[bb + 1];
      if (binMin[bb + 2] < accMinZ) accMinZ = binMin[bb + 2];
      if (binMin[bb + 3] > accMaxX) accMaxX = binMin[bb + 3];
      if (binMin[bb + 4] > accMaxY) accMaxY = binMin[bb + 4];
      if (binMin[bb + 5] > accMaxZ) accMaxZ = binMin[bb + 5];
      accCount += binCount[i];
    }
    rightCount[i] = accCount;
    rightArea[i] = surfaceArea(accMinX, accMinY, accMinZ, accMaxX, accMaxY, accMaxZ);
  }

  const parentArea = surfaceArea(minX, minY, minZ, maxX, maxY, maxZ);
  const leafCost = triCount; // unit per tri intersection
  let bestCost = leafCost;
  let bestSplit = -1; // split between bin (bestSplit) and (bestSplit+1)
  for (let i = 0; i < BIN_COUNT - 1; i++) {
    const lc = leftCount[i];
    const rc = rightCount[i + 1];
    if (lc === 0 || rc === 0) continue;
    // SAH: 1 + (leftArea*leftCount + rightArea*rightCount) / parentArea
    const cost = 1 + (leftArea[i] * lc + rightArea[i + 1] * rc) / parentArea;
    if (cost < bestCost) {
      bestCost = cost;
      bestSplit = i;
    }
  }

  if (bestSplit < 0) {
    // No split improves cost — emit leaf.
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeTriCount[nodeIdx] = triCount;
    return;
  }

  // Partition triangles into left/right by centroid bin index.
  const splitC = cMin + (bestSplit + 1) / scale;
  let left = start;
  let right = end - 1;
  while (left <= right) {
    const c = info.data[left * 10 + centroidOffset];
    if (c < splitC) {
      left++;
    } else {
      swapTri(info.data, left, right);
      right--;
    }
  }
  const mid = left;

  // Safety: degenerate partition (all on one side) → fall back to leaf.
  if (mid === start || mid === end) {
    nodes.nodeLeftFirst[nodeIdx] = start;
    nodes.nodeTriCount[nodeIdx] = triCount;
    return;
  }

  // Reserve child slots consecutively BEFORE recursing — required for the
  // sibling-implicit (right = left + 1) layout to hold.
  const leftChildIdx = nodes.count;
  nodes.count += 2;
  nodes.nodeLeftFirst[nodeIdx] = leftChildIdx;
  nodes.nodeTriCount[nodeIdx] = 0; // mark internal
  buildAt(info, nodes, leftChildIdx, start, mid);
  buildAt(info, nodes, leftChildIdx + 1, mid, end);
}

function surfaceArea(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): number {
  if (minX > maxX || minY > maxY || minZ > maxZ) return 0;
  const dx = maxX - minX;
  const dy = maxY - minY;
  const dz = maxZ - minZ;
  return 2 * (dx * dy + dy * dz + dz * dx);
}

function swapTri(data: Float32Array, a: number, b: number): void {
  if (a === b) return;
  const oa = a * 10;
  const ob = b * 10;
  for (let i = 0; i < 10; i++) {
    const t = data[oa + i];
    data[oa + i] = data[ob + i];
    data[ob + i] = t;
  }
}
